export const SITE_NAME = 'GoCoachify';
export const CANONICAL_ORIGIN = 'https://gocoachify.com';
export const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/og-image.png`;

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: CANONICAL_ORIGIN,
  logo: DEFAULT_OG_IMAGE,
  description:
    'AI-powered coaching platform for life coaches with blogs, tools, and resources that help grow coaching businesses.',
  sameAs: [
    'https://www.facebook.com/gocoachifyai',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: ['English'],
    url: `${CANONICAL_ORIGIN}/contact`,
  },
} as const;

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: CANONICAL_ORIGIN,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${CANONICAL_ORIGIN}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
} as const;

export type CategoryHubSlug =
  | 'coaching-techniques'
  | 'spiritual-coaching'
  | 'coaching-business'
  | 'personal-development';

export interface CategoryHubConfig {
  slug: CategoryHubSlug;
  title: string;
  shortTitle: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  intro: string;
  categoryMatches: string[];
  tagMatches: string[];
  keywordMatches: string[];
  primaryToolSlug: string;
  primaryToolTitle: string;
  primaryToolDescription: string;
}

export const CATEGORY_HUBS: CategoryHubConfig[] = [
  {
    slug: 'coaching-techniques',
    title: 'Coaching Techniques',
    shortTitle: 'Techniques',
    description:
      'Frameworks, session structures, and evidence-informed coaching methods for better client outcomes.',
    seoTitle: 'Coaching Techniques Resources | GoCoachify',
    seoDescription:
      'Explore coaching techniques, frameworks, and exercises for life coaches, executive coaches, and practitioners who want stronger sessions and better outcomes.',
    intro:
      'Build deeper, more structured coaching sessions with practical techniques, models, and conversation frameworks.',
    categoryMatches: ['coaching skills', 'nlp', 'cbt', 'executive coaching', 'coaching tools'],
    tagMatches: ['coaching techniques', 'framework', 'frameworks', 'nlp', 'cbt', 'goal setting', 'active listening'],
    keywordMatches: ['technique', 'framework', 'model', 'session', 'question', 'nlp', 'cbt'],
    primaryToolSlug: 'ai-coaching-question-generator',
    primaryToolTitle: 'AI Coaching Question Generator',
    primaryToolDescription:
      'Generate high-quality coaching questions for discovery, clarity, and action planning.',
  },
  {
    slug: 'spiritual-coaching',
    title: 'Spiritual Coaching',
    shortTitle: 'Spiritual',
    description:
      'Mindfulness, inner work, and reflective practices that support spiritual growth and emotional regulation.',
    seoTitle: 'Spiritual Coaching Guides & Tools | GoCoachify',
    seoDescription:
      'Browse spiritual coaching content, mindfulness practices, and reflective tools for coaches supporting purpose, meaning, and holistic growth.',
    intro:
      'Support meaning-making, mindfulness, and deeper reflection with articles and tools for spiritual coaching journeys.',
    categoryMatches: ['mindfulness', 'hypnotherapy'],
    tagMatches: ['spiritual', 'mindfulness', 'meditation', 'purpose', 'energy', 'healing', 'chakra', 'reiki'],
    keywordMatches: ['spiritual', 'mindfulness', 'meditation', 'healing', 'chakra', 'reiki', 'purpose'],
    primaryToolSlug: 'life-purpose-finder',
    primaryToolTitle: 'Life Purpose Finder',
    primaryToolDescription:
      'Guide clients through strengths, values, and calling-oriented prompts to clarify purpose.',
  },
  {
    slug: 'coaching-business',
    title: 'Coaching Business',
    shortTitle: 'Business',
    description:
      'Marketing, pricing, SEO, systems, and growth strategies for building a sustainable coaching business.',
    seoTitle: 'Coaching Business Growth Hub | GoCoachify',
    seoDescription:
      'Learn how to grow a coaching business with SEO, marketing, pricing, automation, client acquisition, and product-led growth strategies.',
    intro:
      'Turn expertise into growth with business systems, organic marketing, and scalable client acquisition strategies.',
    categoryMatches: ['business', 'business coaching', 'marketing', 'branding', 'ai & technology'],
    tagMatches: ['seo', 'marketing', 'branding', 'sales', 'lead generation', 'automation', 'ai'],
    keywordMatches: ['business', 'marketing', 'seo', 'sales', 'client', 'lead', 'growth', 'automation', 'ai'],
    primaryToolSlug: 'goal-clarity-tool',
    primaryToolTitle: 'Goal Clarity Tool',
    primaryToolDescription:
      'Help coaches and clients turn broad ambitions into focused goals, measures, and next actions.',
  },
  {
    slug: 'personal-development',
    title: 'Personal Development',
    shortTitle: 'Personal Development',
    description:
      'Personal growth content covering confidence, habits, career change, relationships, and life direction.',
    seoTitle: 'Personal Development Coaching Hub | GoCoachify',
    seoDescription:
      'Discover personal development coaching articles and tools for confidence, career clarity, habits, relationships, resilience, and wellbeing.',
    intro:
      'Help clients grow with practical resources for confidence, habits, resilience, relationships, and life direction.',
    categoryMatches: [
      'life coaching',
      'career coaching',
      'health coaching',
      'relationship coaching',
      'financial coaching',
      'parenting coaching',
      'creativity coaching',
      'specialized coaching',
    ],
    tagMatches: ['personal development', 'confidence', 'purpose', 'habits', 'career', 'relationships', 'stress'],
    keywordMatches: ['confidence', 'purpose', 'habit', 'career', 'relationship', 'stress', 'resilience', 'growth'],
    primaryToolSlug: 'wheel-of-life',
    primaryToolTitle: 'Wheel of Life Assessment Tool',
    primaryToolDescription:
      'Assess balance across life areas and turn insight into a practical coaching plan.',
  },
];

export interface MinimalBlogData {
  slug: string;
  title: string;
  category?: string | null;
  tags?: string[] | null;
  excerpt?: string | null;
}

const normalize = (value?: string | null) => (value ?? '').trim().toLowerCase();

export const toAbsoluteUrl = (url: string) =>
  url.startsWith('http') ? url : `${CANONICAL_ORIGIN}${url.startsWith('/') ? url : `/${url}`}`;

export const getCategoryHubBySlug = (slug?: string | null) =>
  CATEGORY_HUBS.find((hub) => hub.slug === slug);

export function inferCategoryHub(post: MinimalBlogData): CategoryHubConfig {
  const category = normalize(post.category);
  const tags = (post.tags ?? []).map((tag) => normalize(tag));
  const haystack = `${normalize(post.title)} ${normalize(post.excerpt)} ${post.slug.toLowerCase()} ${tags.join(' ')}`;

  const scored = CATEGORY_HUBS.map((hub) => {
    let score = 0;

    if (hub.categoryMatches.includes(category)) score += 5;
    score += hub.tagMatches.filter((tag) => tags.some((item) => item.includes(tag))).length * 4;
    score += hub.keywordMatches.filter((keyword) => haystack.includes(keyword)).length * 2;

    return { hub, score };
  }).sort((a, b) => b.score - a.score);

  return scored[0]?.score > 0 ? scored[0].hub : CATEGORY_HUBS[3];
}

export function getHubUrlForPost(post: MinimalBlogData) {
  return `/${inferCategoryHub(post).slug}`;
}

export function getPrimaryToolForPost(post: MinimalBlogData) {
  const hub = inferCategoryHub(post);
  return {
    slug: hub.primaryToolSlug,
    title: hub.primaryToolTitle,
    description: hub.primaryToolDescription,
    href: `/toolkit/${hub.primaryToolSlug}`,
  };
}
